<script setup lang="ts">
/**
 * The Pin Vision icon.
 *
 * Renders a glyph from `src/assets/icons/` as inline SVG, inheriting `currentColor` and sized by
 * the caller (`<PvIcon name="bell" class="h-6 w-6" />`). Attributes fall through to the `<svg>`.
 *
 * A runtime name lookup — rather than per-icon imports — is required, not merely convenient:
 * `KpiCard`'s prop and the admin nav config carry icon names as runtime strings.
 *
 * For PrimeVue's `icon`-family props (`<Button icon>`, `<Tag icon>`), which apply their value as
 * CSS classes to a `<span>` and never instantiate a component, use the generated mask-image
 * classes instead: `icon="pvi pvi-bell"`. Both paths render the same geometry.
 *
 * Both the SVGs and the class sheet come from `npm run icons`.
 */
import type { Component } from 'vue'
import { computed } from 'vue'

const props = defineProps<{
  /** Icon file name without extension, e.g. `golf-cart`. */
  name: string
}>()

// ponytail: eager glob bundles every glyph (~96, a few KB gzipped) because the lookup is by
// runtime string. Move to explicit imports per call site if the set passes a few hundred.
const icons = import.meta.glob<{ default: Component }>('../../assets/icons/*.svg', {
  query: '?component',
  eager: true,
})

const registry = Object.fromEntries(
  Object.entries(icons).map(([path, mod]) => [
    path.split('/').pop()!.replace('.svg', ''),
    mod.default,
  ]),
) as Record<string, Component>

const glyph = computed(() => {
  const found = registry[props.name]
  if (!found && import.meta.env.DEV) {
    console.warn(
      `[PvIcon] unknown icon "${props.name}". Available: ${Object.keys(registry).sort().join(', ')}`,
    )
  }
  return found
})
</script>

<template>
  <!-- Render nothing on an unknown name: a missing icon must never blank the page around it. -->
  <component :is="glyph" v-if="glyph" aria-hidden="true" focusable="false" />
</template>
